import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  ShareableLabel,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Button } from 'primeng/button';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';
import { BdUiLabelsInputComponent } from '@bindoc-projects/labels-lib/ui';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

export interface EditLabelsDialogData {
  shareableType: ShareableType;
  shareableId: string;
  shareableName?: string;
}

export interface EditLabelsDialogResult {
  labels: ShareableLabel[];
}

@Component({
  selector: 'bd-edit-labels-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 p-4 min-w-[420px]">
      @if (loading()) {
        <div class="flex justify-center py-6">
          <p-progress-spinner class="w-10 h-10"></p-progress-spinner>
        </div>
      } @else {
        <bd-ui-labels-input
          inputId="labels-autocomplete"
          [labels]="selectedLabels()"
          [labelOptions]="knownLabels()"
          (labelsChange)="selectedLabels.set($event)"
        ></bd-ui-labels-input>
        <span class="text-xs text-muted-color">
          Vorschläge auswählen oder neuen Text eintippen und mit Enter
          übernehmen.
        </span>
        @if (errorMessage()) {
          <span class="text-bd-error text-sm">{{ errorMessage() }}</span>
        }
        <div class="flex justify-end gap-2 pt-2">
          <p-button
            label="Abbrechen"
            severity="secondary"
            [disabled]="saving()"
            (click)="cancel()"
          ></p-button>
          <p-button
            label="Speichern"
            severity="primary"
            bdDialogPrimaryAction
            [disabled]="saving()"
            (click)="save()"
          ></p-button>
        </div>
      }
    </div>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    BdUiLabelsInputComponent,
    ProgressSpinner,
    Button,
  ],
})
export class BdEditLabelsDialogComponent implements OnInit {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<EditLabelsDialogData>>(DynamicDialogConfig);
  private readonly _shareablesService = inject(BdShareablesService);

  public readonly loading = signal(true);
  public readonly saving = signal(false);
  public readonly errorMessage = signal('');

  /** Label texts currently in the field; colors are restored from the loaded labels on save. */
  public readonly selectedLabels = signal<string[]>([]);
  /**
   * Every label the user can see (this shareable's own labels merged with the
   * org-wide set from `allLabels`), deduped by text — the suggestion pool so a
   * label can be reused across shareables instead of retyped.
   */
  public readonly knownLabels = signal<string[]>([]);

  private _initialLabels: ShareableLabel[] = [];

  ngOnInit(): void {
    const data = this._config.data;
    if (!data?.shareableType || !data?.shareableId) {
      this._ref.close(null);
      return;
    }
    forkJoin({
      current: this._shareablesService.labels(
        data.shareableType,
        data.shareableId,
      ),
      // Degrade gracefully to this shareable's own labels if the global label
      // list is unavailable.
      all: this._shareablesService
        .allLabels()
        .pipe(catchError(() => of<string[]>([]))),
    })
      .pipe(take(1))
      .subscribe({
        next: ({ current, all }) => {
          this._initialLabels = current ?? [];
          const own = this._initialLabels.map((label) => label.text);
          this.selectedLabels.set(own);
          this.knownLabels.set([...new Set([...own, ...(all ?? [])])]);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('Labels konnten nicht geladen werden');
          this.loading.set(false);
        },
      });
  }

  public cancel(): void {
    this._ref.close(null);
  }

  public save(): void {
    if (this.saving()) {
      return;
    }
    const data = this._config.data;
    const normalized = this._normalizeSelection();
    const toAdd = normalized.filter(
      (label) => !this._initialLabels.some((it) => it.text === label.text),
    );
    const toRemove = this._initialLabels.filter(
      (label) => !normalized.some((it) => it.text === label.text),
    );

    if (toAdd.length === 0 && toRemove.length === 0) {
      this._ref.close({ labels: normalized });
      return;
    }

    const operations = [
      ...toRemove.map((label) =>
        this._shareablesService
          .removeLabel(data.shareableType, data.shareableId, label.text)
          .pipe(catchError(() => of(null))),
      ),
      ...toAdd.map((label) =>
        this._shareablesService
          .setLabel(data.shareableType, data.shareableId, label)
          .pipe(catchError(() => of(null))),
      ),
    ];

    this.saving.set(true);
    this.errorMessage.set('');
    forkJoin(operations)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._shareablesService
            .labels(data.shareableType, data.shareableId)
            .pipe(take(1))
            .subscribe({
              next: (labels) =>
                this._ref.close({ labels: labels ?? normalized }),
              error: () => this._ref.close({ labels: normalized }),
            });
        },
        error: () => {
          this.saving.set(false);
          this.errorMessage.set('Labels konnten nicht gespeichert werden');
        },
      });
  }

  /** Trims and dedupes the texts; a label this shareable already had keeps its color. */
  private _normalizeSelection(): ShareableLabel[] {
    const seen = new Set<string>();
    const result: ShareableLabel[] = [];
    for (const entry of this.selectedLabels()) {
      const text = entry.trim();
      if (!text || seen.has(text)) {
        continue;
      }
      seen.add(text);
      const existing = this._initialLabels.find((it) => it.text === text);
      result.push(existing ?? { text, color: null });
    }
    return result;
  }
}
